<template>
  <div
    id="directory-list"
    class="directory-list"
  >
    <TMSHeader
      :title="$t('directory.listTitle', [tenancyInfo.name])"
      :buttons="buttons"
      :needGoback="true"
      :goBack="_goback"
      :fullScreen="true"
    >
      <HKLSelect
        v-if="items.length !== 0"
        slot="before-buttons"
        class="list-filter"
        :disabled="false"
        :required="false"
        :options="filterHklOptions"
        placeholder=""
        :showSearch="true"
        :value="currentFilterIndex"
        @change="selectFilterOption"
      />
      <template
        v-if="histories.length && isEngineer && displayReassignButton"
        #status
      >
        <HKLGrayHint class="under-review-by">
          {{ $t('underReviewBy', [histories[0].detail.value.displayName]) }}
        </HKLGrayHint>
      </template>
    </TMSHeader>
    <div
      v-if="items.length === 0 && loading"
      class="apply"
    >
      <p v-if="canCurrUserCreate">
        {{ $t('directory.msg.applyOne') }}
      </p>
      <p v-else>
        {{ $t('directory.msg.noDir') }}
      </p>
      <button
        v-if="canCurrUserCreate"
        type="button"
        class="btn btn--orange margin-top"
        @click="openCreateModal"
      >{{ $t('action.addDirectory') }}</button>
    </div>

    <div
      v-if="items.length && loading"
      class="items"
    >
      <label class="directory-list__sub-title">
        <span>{{ $t('directory.lobbyDirectory') }}</span>
        <DottedDropdown
          v-if="canCurrUserCreate"
          :id="'lobby-add-dir'"
        >
          <li>
            <a
              class="dot-option-btn"
              @click="() => create(DIR_TYPE.LOBBY)"
            >
              {{ $t('action.addDirectory') }}
            </a>
          </li>
        </DottedDropdown>
      </label>
      <DirectoryTable
        ref="lobbyDirTb"
        :items="lobbyDirectories"
        :originItems="originLobbyDirectories"
        @update:pendingItems="onUpdatePendingItems"
        @refresh:all="refreshList"
        @has:dirCheck="(result) => onDirCheck(DIR_TYPE.LOBBY, result)"
      />

      <label class="directory-list__sub-title">
        <span>{{ $t('directory.floorDirectory') }}</span>
        <DottedDropdown
          v-if="canCurrUserCreate"
          :id="'floor-add-dir'"
        >
          <li>
            <a
              class="dot-option-btn"
              @click="() => create(DIR_TYPE.FLOOR)"
            >
              {{ $t('action.addDirectory') }}
            </a>
          </li>
        </DottedDropdown>
      </label>
      <DirectoryTable
        ref="floorDirTb"
        :tableType="DIR_TYPE.FLOOR"
        :items="floorDirectories"
        :originItems="originFloorDirectories"
        @update:pendingItems="onUpdatePendingItems"
        @refresh:all="refreshList"
        @has:dirCheck="(result) => onDirCheck(DIR_TYPE.FLOOR, result)"
      />
    </div>
    <DirectoryReassignModal
      v-if="buttons.length && canCurrUserApprove"
      ref="reassignModal"
    />
    <ActionConfirmModal
      ref="confirmSubmit"
      modalId="confirmSubmit"
      :confirmTitle="$t('directory.msg.confirmSubmit')"
    />
    <ActionConfirmModal
      ref="confirmNotifyModal"
      :modalId="tenancyId + '-dir-approval'"
      :confirmTitle="$t('directory.msg.confirmNotify')"
    />

    <CreateDirectoryModal
      v-if="canCurrUserCreate"
      ref="createDirectoryModal"
      :tenancyId="tenancyId"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { ref, computed } from 'vue-demi'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import DottedDropdown from '@/controls/DottedDropdown'
import TMSHeader from '@/controls/TMSHeader'
import { useRefModal } from '@/controls/Modal/modal.hook'
import HKLSelect from '@/controls/Select/HKLSelect'
import dateUtil from '@/utils/dateUtil'
import mutationTypes from '@/store/mutation-types'
import navigation from '@/utils/navigation'
import notification from '@/utils/notification'
import { sendEmail } from '@/api/postman/request'
import { directoryRouteNames, EmailTypes } from '@/utils/consts'
import { getTenancy } from '@/api/tenancies/request'
import { getDirectorySubmitter } from '@/api/postman/directories'
import { utils as formUtils } from '@/plugin/InputChange'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import HKLGrayHint from '@/controls/HKLGrayHint'
import { useRouteParamId } from '@/router/useRouter'
import DirectoryReassignModal from './DirectoryReassignModal'
import { getDirStatus, isDuplicatedDirectory, setTenancyInfoForDirectory } from './utils'
import { DIR_TYPE, DIR_STATUS, DIR_ACTION, DIR_COMP_TYPE, readPermitHolderVisibleStatusMap } from './consts'
import DirectoryTable from './DirectoryTable'
import CreateDirectoryModal from './CreateDirectoryModal'
import { useUnderReviewerHistories } from './hook'

export default {
  name: 'DirectoriesList',
  components: {
    ActionConfirmModal,
    CreateDirectoryModal,
    DottedDropdown,
    DirectoryTable,
    TMSHeader,
    HKLSelect,
    DirectoryReassignModal,
    HKLGrayHint,
  },

  setup () {
    let returnData = {}
    const { open: openReassignModal, modal: reassignModal } = useRefModal()
    returnData = {
      ...returnData,
      openReassignModal,
      reassignModal,
    }
    const tenancyIdRef = useRouteParamId()
    const { data: histories } = useUnderReviewerHistories(tenancyIdRef)
    returnData = {
      ...returnData,
      histories,
    }

    const DirCheckMap = ref({
      [DIR_TYPE.LOBBY]: false,
      [DIR_TYPE.FLOOR]: false,
    })
    const onDirCheck = (type, status) => {
      DirCheckMap.value[type] = status
    }
    const displayReassignButton = computed(() => {
      return Object.values(DirCheckMap.value).some((s) => s)
    })
    returnData = {
      ...returnData,
      displayReassignButton,
      onDirCheck,
    }

    return returnData
  },

  data () {
    const filterOptions = [{
      label: this.$t('directory.listFilterOption.allActiveAndPublished'),
      filter: {
        exclude: [{
          status: DIR_STATUS.PUBLISHED,
          action: DIR_ACTION.REMOVE,
        }],
      },
    },
    {
      label: this.$t('directory.listFilterOption.allPublished'),
      filter: {
        include: [{
          status: DIR_STATUS.PUBLISHED,
          action: DIR_ACTION.ADD,
        },
        {
          status: DIR_STATUS.CART,
          action: DIR_ACTION.REMOVE,
        },
        {
          status: DIR_STATUS.SUBMITTED,
          action: DIR_ACTION.REMOVE,
        },
        {
          status: DIR_STATUS.APPR,
          action: DIR_ACTION.REMOVE,
        },
        {
          status: DIR_STATUS.PROCESSED,
          action: DIR_ACTION.REMOVE,
        }],
      },
    },
    {
      label: this.$t('directory.listFilterOption.allRemoved'),
      filter: {
        include: [{
          status: DIR_STATUS.PUBLISHED,
          action: DIR_ACTION.REMOVE,
        }],
      },
    }]
    return {
      apprBtnName: '',
      comment: '',
      currentFilterOption: filterOptions[0],
      dirTableRefs: ['lobbyDirTb', 'floorDirTb'],
      DIR_TYPE,
      DIR_ACTION,
      emptyValue: this.$t('emptyValue'),
      filterOptions,
      items: [],
      loading: false,
      pendingIndices: {},
      pendingFloorApprs: [],
      pendingFloorRejects: [],
      pendingLobbyApprs: [],
      pendingLobbyRejects: [],
      tenancyId: null,
    }
  },

  computed: {
    ...mapGetters('user', ['isTenant', 'isEngineer', 'currentUser']),
    ...mapGetters('directories', ['tenancyInfo', 'canCurrUserApprove', 'canCurrUserCreate']),
    buttons () {
      if (this.items.length === 0) {
        return []
      }
      let btns = []
      if (this.canCurrUserCreate && !this.canCurrUserApprove) {
        btns.push({
          title: this.$t('action.sendForApproval'),
          cls: 'btn--orange',
          func: this.openConfirmModal,
          disable: !this.hasPendingApprItems,
        })
      }
      if (this.canCurrUserApprove) {
        const title = `${this.$t('action.confirm')} & ${this.$t('action.notify')}`
        btns.push({
          title,
          cls: 'btn--orange',
          func: this.openConfirmNotifyModal,
          disable: !this.hasPendingItems,
        })
        if (this.displayReassignButton) {
          btns.push({
            title: this.$t('action.reAssign'),
            cls: 'btn--orange',
            func: () => this.openReassignModal(),
          })
        }
      }
      return btns
    },
    hasPendingItems () {
      return this.pendingLobbyRejects.length || this.pendingLobbyApprs.length || this.pendingFloorRejects.length || this.pendingFloorApprs.length
    },
    hasPendingApprItems () {
      return this.items.some(item => {
        if (item.child) {
          let indexOfChild = this.items.findIndex((element) => element.id === item.child.id)
          return item.status === DIR_STATUS.CART && this.items[indexOfChild].status === DIR_STATUS.CART
        } else {
          return item.status === DIR_STATUS.CART
        }
      })
    },
    filteredDirectories () {
      const formattedItems = this.formatDirectory(this.items)
      if (!this.currentFilterOption) {
        return formattedItems
      }
      const include = this.currentFilterOption.filter.include
      const exclude = this.currentFilterOption.filter.exclude || []
      const fitCase = (item, i) => {
        if (typeof i === 'string') {
          return i === item.status
        } else if (typeof i === 'object') {
          return i.status === item.status && i.action === item.action
        } else {
          return false
        }
      }
      return formattedItems.filter(item => {
        const includedFlag = include ? include.some(i => fitCase(item, i)) : true
        const excludedFlag = exclude.some(i => fitCase(item, i))
        return includedFlag ? !excludedFlag : false
      })
    },
    floorDirectories () {
      return this.getItemsByType(this.filteredDirectories, DIR_TYPE.FLOOR)
    },
    lobbyDirectories () {
      return this.getItemsByType(this.filteredDirectories, DIR_TYPE.LOBBY)
    },
    filterHklOptions () {
      return this.filterOptions.map((option, index) => ({
        name: option.label,
        value: index,
      }))
    },
    currentFilterIndex () {
      return this.filterOptions.findIndex(option => option === this.currentFilterOption)
    },
    originFloorDirectories () {
      return this.getItemsByType(this.items, DIR_TYPE.FLOOR)
    },
    originLobbyDirectories () {
      return this.getItemsByType(this.items, DIR_TYPE.LOBBY)
    },
  },

  async mounted () {
    this.tenancyId = this.$router.currentRoute.params.id
    await this.refreshList()
  },

  methods: {
    ...mapActions('directories', ['loadAll', 'batchUpdate', 'createDirectory']),
    ...mapMutations('directories', {
      setTenancyInfo: mutationTypes.DIRECTORY_TENANCY_INFO,
    }),
    _goback () {
      this.$router.goBack()
    },
    openConfirmModal () {
      this.$refs['confirmSubmit'].open(this.submit)
    },
    openConfirmNotifyModal () {
      this.$refs['confirmNotifyModal'].open(this.confirmNotify)
    },
    openCreateModal () {
      this.$refs['createDirectoryModal'].open()
    },
    selectFilterOption (index) {
      this.currentFilterOption = this.filterOptions[index] || this.filterOptions[0]
    },
    async confirmNotify () {
      const pendingApprs = this.pendingLobbyApprs.concat(this.pendingFloorApprs)
      pendingApprs.forEach(item => { item.status = DIR_STATUS.APPR })
      const pendingRejects = this.pendingLobbyRejects.concat(this.pendingFloorRejects)
      pendingRejects.forEach(item => { item.status = DIR_STATUS.REJECTED })
      if (pendingApprs.length || pendingRejects.length) {
        await this.batchUpdate({request: {
          directories: pendingApprs.concat(pendingRejects),
        }})
      }
      const groupBySubmitterFn = item => lodash.get(getDirectorySubmitter(item), 'email', item.id)

      if (pendingApprs.length) {
        await sendEmail(EmailTypes.directories.waitForProcessing, pendingApprs)
      }

      // sendEmails to notify submitter of the rejection
      await Promise.all(
        Object.values(lodash.groupBy(pendingRejects, groupBySubmitterFn)).map(
          groupedDirectories => sendEmail(EmailTypes.directories.rejected, groupedDirectories),
        ),
      )

      await this.refreshList()
      this.pendingLobbyApprs = []
      this.pendingFloorApprs = []
      this.pendingFloorRejects = []
      this.pendingLobbyRejects = []
      formUtils.resetDirty()
    },
    async create (type) {
      if (!this.tenancyId) {
        notification.warn(this.$t('directory.msg.invalidTenancyId'))
        return
      }
      try {
        const directory = await this.createDirectory({
          tenancyId: this.tenancyId,
          type,
          companies: [{
            type: DIR_COMP_TYPE.MAIN,
          }],
          action: DIR_ACTION.ADD,
        })
        const toPage = this.isTenant ? directoryRouteNames.tenantDirectoryDetail : directoryRouteNames.directoryDetail
        navigation.toPage(toPage, { id: directory.id })
      } catch (error) {
        // do nothing
      }
    },
    setComment (action) {
      const updatedAt = new Date()
      const actionBy = this.currentUser.name
      this.pendingItems.forEach(item => {
        item.actionHistory = {
          actionBy,
          action,
          comment: this.comment,
          updatedAt,
        }
      })
    },
    parseItem (item = {}) {
      return {
        code: item.code || this.emptyValue,
        status: getDirStatus(item, this.isTenant, '<br>'),
        submittedAt: item.submittedAt ? dateUtil.formatDate(item.submittedAt) : this.emptyValue,
        effectiveDate: item.effectiveDate ? dateUtil.formatDate(item.effectiveDate) : this.emptyValue,
        removeDate: item.removeDate ? dateUtil.formatDate(item.removeDate) : this.emptyValue,
        publishedAt: item.publishedAt ? dateUtil.formatDate(item.publishedAt) : this.emptyValue,
      }
    },
    getItemsByType (items = [], type) {
      return items.filter(item => item.type === type)
        .map(item => {
          const isFloorDR = type === DIR_TYPE.FLOOR
          const companies = item.companies || []
          if (companies.length) {
            item.companies = companies.map(company => {
              return {
                ...company,
                floor: (isFloorDR ? item.floor : company.floor) || this.emptyValue,
                ...this.parseItem(item),
              }
            })
          } else {
            item.companies = [{
              floor: this.emptyValue,
              engName: this.emptyValue,
              chtName: this.emptyValue,
              ...this.parseItem(item),
            }]
          }
          return item
        })
    },
    /**
     * This function is used to add its child directory to the 'edited' ('removeAndDuplicate') directory.
     */
    formatDirectory (unformattedItems) {
      const formattedItems = lodash.cloneDeep(unformattedItems)
      const itemMap = lodash.keyBy(formattedItems, 'id')
      formattedItems.forEach(item => {
        const parent = itemMap[item.sourceId]
        if (isDuplicatedDirectory(item, parent)) {
          if (parent) {
            parent.child = item
          }
        }
      })
      return formattedItems
    },
    async submit () {
      const directoriesInCart = this.items.filter(item => {
        if (item.child) {
          let indexOfChild = this.items.findIndex((element) => element.id === item.child.id)
          return item.status === DIR_STATUS.CART && this.items[indexOfChild].status === DIR_STATUS.CART
        } else {
          return item.status === DIR_STATUS.CART
        }
      })
      directoriesInCart.forEach(item => {
        item.status = DIR_STATUS.SUBMITTED
        item.submittedAt = new Date()
      })
      await this.batchUpdate({request: {
        directories: directoriesInCart,
      }})
      await sendEmail(EmailTypes.directories.waitForApproval, directoriesInCart)
      await sendEmail(EmailTypes.directories.submitted, directoriesInCart)
      this.$forceUpdate()
      await this.refreshList()
    },

    async loadTenancyInfo () {
      if (this.items && this.items.length) {
        const itemWithTenancy = this.items.find(item => item.tenancy)
        if (itemWithTenancy) {
          this.setTenancyInfo(
            setTenancyInfoForDirectory(itemWithTenancy.tenancy))
          return
        }
      }
      const tenancy = await getTenancy(this.tenancyId, true)
      this.setTenancyInfo(
        setTenancyInfoForDirectory(tenancy))
    },
    async refreshList () {
      const directories = await this.loadAll({ tenancyId: this.tenancyId })
      this.items = directories.filter(this.itemVisibleFilter)
      await this.loadTenancyInfo()
      this.loading = true
    },
    itemVisibleFilter (item) {
      const canEngineerReadAll = aclHelper.getAccess(tmsPermsTypes.directory.readAllStatus)

      // Staff with create/update or readAllStatus permission can view all items
      if (this.canCurrUserCreate || canEngineerReadAll) return true

      // For staff without create/update permission
      const visibleStatus = readPermitHolderVisibleStatusMap[item.action]
      return visibleStatus && visibleStatus.includes(item.status)
    },
    onUpdatePendingItems (isApprItems, tableType, items) {
      const itemArr = lodash.values(items)
      if (isApprItems) {
        tableType === DIR_TYPE.LOBBY ? (this.pendingLobbyApprs = itemArr) : (this.pendingFloorApprs = itemArr)
      } else {
        tableType === DIR_TYPE.LOBBY ? (this.pendingLobbyRejects = itemArr) : (this.pendingFloorRejects = itemArr)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/common';

.directory-list {
  & ::v-deep .tms-header__box.tms-header__box-fullscreen {
    padding-left: 15px;
    padding-right: 15px;
  }

  .apply {
    width: 460px;
    height: 120px;
    text-align: center;
    position: absolute;
    left: calc(50% - 200px);
    top: calc(50% - 60px);
    p {
      text-align: center;
      font-size: 16px;
    }
    p + p {
      margin: 20px 0 0;
    }
  }
  .list-filter {
    width: 280px;
    display: inline-block;
    vertical-align: middle;
  }
  .margin-top {
    margin: 15px 0 0;
  }

  .back {
    background: white;
    font-family: Verdana;
    color: #0c3a58;
    padding: 10px 15px 20px 15px;

    span {
      font-family: Verdana;
      padding-top: 15px;
      font-size: 14px;
      color: #0C3A58;
      cursor: pointer;
    }
  }

  .items {
    padding: 0 20px;
  }

  &__sub-title {
    display: flex;
    align-items: center
  }
}

.under-review-by {
  margin-bottom: -20px;
  padding-top: 20px;
  height: 20px;
  line-height: 20px;
}
</style>
